import '../styles/pdf-pages.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateVenueCo2 } from '@/domain/impact-calculate-venue/entities/calculate-venue-co2.entity';
import { CalculateVenueEss } from '@/domain/impact-calculate-venue/entities/calculate-venue-ess.entity';
import {
  selectImpactCalculateVenueCo2ForProject,
  selectImpactCalculateVenueEssForProject,
} from '@/domain/impact-calculate-venue/impact-calculate-venue.selectors';
import { selectImpactEssAnswersMap } from '@/domain/impact-ess/impact-ess.selectors';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatProjectDatesTz } from '@/ui/utils/format-project-dates';

import CalendarMonthSvg from '../assets/calendar_month.svg';
import { LittleGauge } from '../components/LittleGauge';
import { ParityBar } from '../components/ParityBar';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import { extendVenueEssCalculate } from '../utils/compute-extend-ess';
import { displayDecimal } from '../utils/display-scores.utils';
import {
  computeTotalPermanentTeamParity,
  Parity,
} from '../utils/team-parity.utils';

export function PdfVenuePage4({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const venue = useAppSelector(selectCurrentProjectVenue);
  const venueId = venue?.id ?? 0;

  const impactEss = extendVenueEssCalculate(
    useAppSelector(selectImpactCalculateVenueEssForProject(venueId)),
  ) satisfies CalculateVenueEss;

  const impactCo2 = useAppSelector(
    selectImpactCalculateVenueCo2ForProject(venueId),
  ) satisfies CalculateVenueCo2;

  const ESS_ANSWERS = useAppSelector(selectImpactEssAnswersMap);
  const ESS_3 = ESS_ANSWERS?.get('ESS_3')?.value || '';

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!venue) {
    return <h1>{translate('PdfExports.venue-not-found')}</h1>;
  }

  const impactEssScore100 = Math.round(
    (impactEss.global.score * 100) / impactEss.global.maxScore,
  );

  const totalArtisticTeamParity: Parity = Object.values(impactCo2.dates.byMonth)
    .filter((item) => item !== undefined)
    .reduce(
      (acc, item) => {
        acc.men += item.totalMen || 0;
        acc.women += item.totalWomen || 0;
        acc.other += item.totalOther || 0;
        return acc;
      },
      { men: 0, women: 0, other: 0 },
    );

  const totalPermanentTeamParity = computeTotalPermanentTeamParity(ESS_3);

  return (
    <div id="page4" className="pdf-page">
      <div className="content">
        <div className="pdf-light-header">
          <div>
            <h1>{venue.name}</h1>
            {venue.startDate && venue.endDate && (
              <h2>
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="venue-date">
                  {formatProjectDatesTz(
                    venue.startDate || '',
                    venue.endDate || '',
                  )}
                </span>
              </h2>
            )}
          </div>
        </div>
        <div className="pdf-separator"></div>

        <div className="model-header">
          <div>
            <h2>{translate('PdfExports.ess-score-details')}</h2>
          </div>
          <div>
            <h4>
              <span className="highlight">{impactEssScore100}</span>/ 100
            </h4>
            <h5>
              {impactEss.global.score} / {impactEss.global.maxScore}
            </h5>
          </div>
          <div>
            <PdfScoreCard
              type="ess"
              scoreLetter={impactEss.global.scoreLetter}
              size="medium"
            />
          </div>
        </div>

        <div className="model-content">
          <div
            style={{
              width: 360,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="sector">
              <div className="sector-header">
                <h4 style={{ width: 240 }}>
                  {translate('Ess.Topics.PARITY_EQUALITY')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(
                      impactEss.structure.topics.PARITY_EQUALITY.score,
                    )}
                  </span>{' '}
                  / {impactEss.structure.topics.PARITY_EQUALITY.maxScore}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  <li key="tmp-combined-artistic-team">
                    <span>Équipe Artistique et technique</span>
                    <span>
                      {displayDecimal(impactEss.dates.score)} /{' '}
                      {impactEss.dates.maxScore}
                    </span>
                  </li>
                  {(
                    Object.entries(
                      impactEss.structure.topics.PARITY_EQUALITY.sections,
                    ) as [
                      keyof typeof impactEss.structure.topics.PARITY_EQUALITY.sections,
                      { score: number; maxScore: number },
                    ][]
                  )
                    .filter(
                      ([key]) =>
                        ![
                          'ARTISTIC_TEAM',
                          'OCCASIONAL_OPERATIONAL_TEAM',
                        ].includes(key),
                    )
                    .map(([key, { score, maxScore }]) => (
                      <li key={key}>
                        <span>{translate(`Ess.Sections.${key}`)}</span>
                        <span>
                          {displayDecimal(score)} / {maxScore}
                        </span>
                      </li>
                    ))}
                </ul>
                <LittleGauge
                  score={impactEss.structure.topics.PARITY_EQUALITY.score}
                  maxScore={impactEss.structure.topics.PARITY_EQUALITY.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4 style={{ width: 240 }}>
                  {translate('Ess.Topics.ACCESSIBILITY_SOCIAL_DIALOGUE')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(
                      impactEss.structure.topics.ACCESSIBILITY_SOCIAL_DIALOGUE
                        .score,
                    )}
                  </span>{' '}
                  /{' '}
                  {
                    impactEss.structure.topics.ACCESSIBILITY_SOCIAL_DIALOGUE
                      .maxScore
                  }
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEss.structure.topics.ACCESSIBILITY_SOCIAL_DIALOGUE
                        .sections,
                    ) as [
                      keyof typeof impactEss.structure.topics.ACCESSIBILITY_SOCIAL_DIALOGUE.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Ess.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {maxScore}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={
                    impactEss.structure.topics.ACCESSIBILITY_SOCIAL_DIALOGUE
                      .score
                  }
                  maxScore={
                    impactEss.structure.topics.ACCESSIBILITY_SOCIAL_DIALOGUE
                      .maxScore
                  }
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4 style={{ width: 240 }}>
                  {translate('Ess.Topics.LOCAL_IMPACT_RSE_ENGAGEMENT')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(
                      impactEss.structure.topics.LOCAL_IMPACT_RSE_ENGAGEMENT
                        .score,
                    )}
                  </span>{' '}
                  /{' '}
                  {
                    impactEss.structure.topics.LOCAL_IMPACT_RSE_ENGAGEMENT
                      .maxScore
                  }
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      impactEss.structure.topics.LOCAL_IMPACT_RSE_ENGAGEMENT
                        .sections,
                    ) as [
                      keyof typeof impactEss.structure.topics.LOCAL_IMPACT_RSE_ENGAGEMENT.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Ess.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {maxScore}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={
                    impactEss.structure.topics.LOCAL_IMPACT_RSE_ENGAGEMENT.score
                  }
                  maxScore={
                    impactEss.structure.topics.LOCAL_IMPACT_RSE_ENGAGEMENT
                      .maxScore
                  }
                />
              </div>
            </div>
          </div>
          <div
            style={{
              width: 340,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="sector" style={{ flex: '0 0 auto' }}>
              <div className="sector-header">
                <h4 style={{ width: 240 }}>Parité des équipes</h4>
              </div>
              <div className="sector-value" style={{ padding: '10px 10px' }}>
                <h5 style={{ margin: '0', paddingBottom: '6px' }}>
                  Équipe permanente
                </h5>
                {/* @ts-ignore */}
                <ParityBar data={{ parity: totalPermanentTeamParity }} />
                <h5 style={{ margin: '10px 0 0 0', paddingBottom: '6px' }}>
                  Équipe artistique et technique
                </h5>
                <ParityBar data={{ parity: totalArtisticTeamParity }} />
              </div>
            </div>
            <div className="model-overall-comments">
              {translate('PdfExports.comment')}
            </div>
          </div>
        </div>

        <div className="navigation-controls">
          <a href="#page3" className="tag-pill">
            {translate('PdfExports.go-back-to-env')}
          </a>
          {/* TODO: cette section ne doit être active que si l'utilisateur a activé l'export des réponses */}
          {/* <a href="#page5" className="tag-pill">
            {translate('PdfExports.go-to-question-answers')}
          </a> */}
        </div>
      </div>

      <PdfProjectPager current={4} total={totalPages}></PdfProjectPager>
    </div>
  );
}
